import React, { useContext, useState } from 'react';
import useStyles from './styles';
import { useApolloClient, useQuery } from '@apollo/react-hooks';
import { FloatingRates } from '../../graphql/rates/types/FloatingRates';
import { GET_FLOATING_RATES } from '../../graphql/rates/query';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';

import EditIcon from '@material-ui/icons/Edit';
import EditTEG from './EditTEG';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import EditOtherTaxes from './EditOtherTaxes';
import clsx from 'clsx';
import { fixedDecimalPercentage } from '../../common';
import { ApiErrorCode, Operation, Resource } from '../../common/constants';
import {
  displaySnackBar,
  SnackVariableInterface,
} from '../../common/displaySnackbar';
import IconButton from '@material-ui/core/IconButton';
import { IUserContext, UserContext } from '../../providers/UserProvider';
import { useTheme } from '@material-ui/core';
import { base64Logo } from './utils/logo';

interface IMandataryRate {
  year: number;
  exellentRate: number;
  teg: number;
  goodRate: number;
}

const createData = (
  year: number,
  exellentRate: number | null,
  teg: number,
  goodRate: number | null,
): IMandataryRate => {
  return {
    year,
    exellentRate: exellentRate || 0,
    teg,
    goodRate: goodRate || 0,
  };
};

export const otherRatesInfo = {
  "Taux d'assurance par tranches d'âges": [
    {
      label: 'Moins de 30 ans',
      key: 'firstInsurance',
      groupLabel: "Taux d'assurance par tranches d'âges",
    },
    {
      label: '30 à 45 ans',
      key: 'secondInsurance',
      groupLabel: "Taux d'assurance par tranches d'âges",
    },
    {
      label: 'Plus de 45 ans',
      key: 'thirdInsurance',
      groupLabel: "Taux d'assurance par tranches d'âges",
    },
  ],

  'Taux par tranches (frais de notaire)': [
    {
      label: 'De 0 à 6.500 euros',
      key: 'firstInstallment',
      groupLabel: 'Taux par tranches (frais de notaire)',
    },
    {
      label: 'De 6.501 à 17.000 euros',
      key: 'secondInstallment',
      groupLabel: 'Taux par tranches (frais de notaire)',
    },
    {
      label: 'De 17.001 à 60.000 euros',
      key: 'thirdInstallment',
      groupLabel: 'Taux par tranches (frais de notaire)',
    },
    {
      label: '60.001 euros et plus',
      key: 'fourthInstallment',
      groupLabel: 'Taux par tranches (frais de notaire)',
    },
  ],

  Autres: [
    { label: 'T.V.A.', key: 'vat', groupLabel: 'Autres' },

    {
      label: 'Indre (36), l’Isère (38), le Morbihan (56), et Mayotte (976)',
      key: 'specificOldTax',
      groupLabel: 'Autres',
    },

    {
      label: 'Autres Département(Ancien)',
      key: 'otherOldTax',
      groupLabel: 'Autres',
    },

    { label: 'Taxe (Neuf)', key: 'newTax', groupLabel: 'Autres' },

    {
      label: 'Sécurisation Immobilière',
      key: 'realEstateSecurity',
      groupLabel: 'Autres',
    },

    // {
    //   label: 'Honoraires (en Euro)',
    //   key: 'honoraires',
    //   groupLabel: 'Autres',
    // },
  ],
};

const Parameters = () => {
  const snackBarData: SnackVariableInterface = {
    type: 'ERROR',
    message: '',
    isOpen: true,
  };

  const { canI } = useContext<IUserContext>(UserContext);
  const client = useApolloClient();
  const classes = useStyles();
  const theme = useTheme();

  const { data, error, loading } = useQuery<FloatingRates>(GET_FLOATING_RATES, {
    onError: (error) => {
      snackBarData.message = "Une erreur inconnue s'est produite."; // TODO: internationalisation

      if (error.graphQLErrors) {
        if (error.graphQLErrors.length) {
          const currentError = error.graphQLErrors[0];

          if (currentError.extensions && currentError.extensions.code) {
            switch (currentError.extensions.code) {
              case ApiErrorCode.INTERNAL_SERVER_ERROR:
                snackBarData.message = 'Erreur interne du serveur.';
                break;

              case ApiErrorCode.INVALID_TOKEN:
              case ApiErrorCode.BAD_USER_INPUT:
                return;

              case ApiErrorCode.UNAUTHENTICATED:
                snackBarData.message =
                  'Vous devez vous connecter pour effectuer cette opération.';
                break;

              // case UKNOWN_ERROR:
              //   break;
            }
          }
        } else if (error.networkError) {
          snackBarData.message =
            'Erreur de réseau. Veuillez vérifier votre connexion internet.';
        }
      }

      displaySnackBar(client, snackBarData);
    },
  });

  const [openEditMandataryRateDialog, setOpenEditMandataryRateDialog] =
    useState<boolean>(false);
  const [currentMandataryRate, setCurrentMandataryRate] =
    useState<IMandataryRate | null>(null);

  const [openEditOtherRatesDialog, setOpenEditOtherRatesDialog] =
    useState<boolean>(false);

  if (loading) return <p>Chargement...</p>;
  if (error || !data) return <p>Aucune donnée disponible</p>;

  const mandataryRates = data.floatingRates.mandataryRates.map(
    (mandataryRate) =>
      createData(
        mandataryRate.year,
        mandataryRate.exellentRate,
        mandataryRate.teg,
        mandataryRate.goodRate,
      ),
  );

  // Return only less than or equal to 25 years rate
  const mandataryRatesFiltered = mandataryRates.filter((m) => +m.year <= 25);

  const handleEditMandataryRate = (selectedMandataryRate: IMandataryRate) => {
    setCurrentMandataryRate(selectedMandataryRate);
    setOpenEditMandataryRateDialog(true);
  };

  const handleClose = () => {
    setCurrentMandataryRate(null);
    setOpenEditMandataryRateDialog(false);
    setOpenEditOtherRatesDialog(false);
  };

  const handleGeneratePdf = () => {
    const doc = new jsPDF();

    const tableStyle = {
      headStyles: {
        fontStyle: 'bold' as 'bold',
        fillColor: theme.palette.primary.main,
        textColor: theme.palette.common.white,
        cellPadding: theme.spacing(1),
      },
      bodyStyles: {
        cellPadding: theme.spacing(1),
        fontSize: 12,
      },
    };

    doc.addImage(base64Logo, 'PNG', 70, 10, 70, 25);

    // Data to print in the pdf

    const mandataryRatesColumns: string[][] = mandataryRatesFiltered.map(
      (rate) => [
        `${rate.year} ans`,
        fixedDecimalPercentage(rate.exellentRate),
        fixedDecimalPercentage(rate.teg),
        fixedDecimalPercentage(rate.goodRate),
      ],
    );

    const floatingRatesColumns = [
      [
        'Moins de 30 ans',
        fixedDecimalPercentage(data?.floatingRates?.firstInsurance),
      ],
      [
        '30 à 45 ans',
        fixedDecimalPercentage(data?.floatingRates?.secondInsurance),
      ],
      [
        'Plus de 45 ans',
        fixedDecimalPercentage(data?.floatingRates?.thirdInsurance),
      ],
    ];

    const installmentRatesColumns = [
      [
        '0 à 6.500 euros',
        fixedDecimalPercentage(data?.floatingRates?.firstInstallment),
      ],
      [
        '6.501 à 17.000 euros',
        fixedDecimalPercentage(data?.floatingRates?.secondInstallment),
      ],
      [
        '17.001 à 60.000 euros',
        fixedDecimalPercentage(data?.floatingRates?.thirdInstallment),
      ],
      [
        '60.001 euros et plus',
        fixedDecimalPercentage(data?.floatingRates?.fourthInstallment),
      ],
    ];

    const otherRatesColumns: string[][] = [
      ['T.V.A', fixedDecimalPercentage(data?.floatingRates?.vat)],
      [
        'Indre (36), l’Isère (38), le Morbihan (56), et Mayotte (976)',
        fixedDecimalPercentage(data?.floatingRates?.specificOldTax),
      ],
      [
        'Autres Département(Ancien)',
        fixedDecimalPercentage(data?.floatingRates?.otherOldTax),
      ],
      ['Taxe (Neuf)', fixedDecimalPercentage(data?.floatingRates?.newTax)],
      [
        'Sécurisation Immobilière',
        fixedDecimalPercentage(data?.floatingRates?.realEstateSecurity),
      ],
    ];

    // data tables

    autoTable(doc, {
      head: [
        [
          {
            content: 'Taux immobiliers moyens',
            colSpan: 4,
            styles: {
              halign: 'center',
              fontSize: 13,
              textColor: theme.palette.primary.main,
              fillColor: theme.palette.common.white,
              cellPadding: { top: 0, bottom: 10 },
            },
          },
        ],
        ['Durée', 'Taux excellent', 'Taux très bon', 'Taux bon'],
      ],
      body: mandataryRatesColumns,
      showHead: 'firstPage',
      startY: 45,

      // styles
      ...tableStyle,
    });

    // @ts-ignore
    const endMandataryRatesTable = doc.lastAutoTable.finalY;

    autoTable(doc, {
      head: [
        [
          {
            content: "Taux d'assurance par tranches d'âges",
            colSpan: 2,
            styles: { halign: 'center', fontSize: 13 },
          },
        ],
      ],
      body: floatingRatesColumns,
      showHead: 'firstPage',
      startY: endMandataryRatesTable + 70,
      // styles
      ...tableStyle,
    });

    // @ts-ignore
    const endFloatingTable = doc.lastAutoTable.finalY;

    autoTable(doc, {
      head: [
        [
          {
            content: 'Taux par tranches (frais de notaire)',
            colSpan: 2,
            styles: { halign: 'center', fontSize: 13 },
          },
        ],
      ],
      body: installmentRatesColumns,
      showHead: 'firstPage',
      startY: endFloatingTable + 10,

      // styles
      ...tableStyle,
    });

    // @ts-ignore
    const endOtherTable = doc.lastAutoTable.finalY;

    autoTable(doc, {
      head: [
        [
          {
            content: 'Autres',
            colSpan: 2,
            styles: { halign: 'center', fontSize: 13 },
          },
        ],
      ],
      body: otherRatesColumns,
      showHead: 'firstPage',
      startY: endOtherTable + 10,

      // styles
      ...tableStyle,
    });

    // print all tables

    doc.save('barème-taux-connectCrédit.pdf');
  };

  const canEditRates = canI(Operation.UPDATE, Resource.RATES);

  return (
    <Box {...{ display: 'flex' }}>
      <Box {...{ flex: 1, padding: 5 }}>
        <Typography
          {...{
            variant: 'h5',
            className: clsx(classes.underlined),
            style: { marginBottom: '20px' },
          }}>
          Taux immobiliers moyens :
        </Typography>

        <TableContainer
          {...{
            component: Paper,
            className: clsx(classes.tableContainer, 'grey-scroll-bar'),
          }}>
          <Table stickyHeader aria-label="teg rates">
            <TableHead>
              <TableRow>
                <TableCell>Durée</TableCell>
                <TableCell align="right">Taux excellent</TableCell>
                <TableCell align="right">Taux très bon</TableCell>
                <TableCell align="right">Taux bon</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {mandataryRatesFiltered.map((mandataryRateItem) => (
                <TableRow key={mandataryRateItem.year}>
                  <TableCell component="th" scope="row">
                    {mandataryRateItem.year} ans
                  </TableCell>

                  <TableCell align="right">
                    {fixedDecimalPercentage(mandataryRateItem.exellentRate)}
                  </TableCell>

                  <TableCell align="right">
                    {fixedDecimalPercentage(mandataryRateItem.teg)}
                  </TableCell>

                  <TableCell align="right">
                    {fixedDecimalPercentage(mandataryRateItem.goodRate)}
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      disabled={!canEditRates}
                      color="primary"
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        handleEditMandataryRate(mandataryRateItem)
                      }>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {openEditMandataryRateDialog && currentMandataryRate && (
        <EditTEG
          {...{
            excellentRate: currentMandataryRate.exellentRate,
            teg: currentMandataryRate.teg,
            goodRate: currentMandataryRate.goodRate,
            year: currentMandataryRate.year,
            handleClose,
          }}
        />
      )}

      {openEditOtherRatesDialog && (
        <EditOtherTaxes {...{ otherRates: data.floatingRates, handleClose }} />
      )}

      <Box
        {...{ flex: 1, padding: 5, className: clsx(classes.tableContainer) }}>
        <Typography {...{ variant: 'h5', className: clsx(classes.underlined) }}>
          Autres taux :
        </Typography>

        {Object.keys(otherRatesInfo).map((key) => {
          return (
            <>
              <Typography {...{ key, className: classes.groupLabel }}>
                {key}
              </Typography>

              {(otherRatesInfo as any)[key].map(
                (rateInfo: any, index: number) => {
                  if (rateInfo.key === 'honoraires') {
                    return (
                      <Box {...{ display: 'flex', key: index }}>
                        <Typography
                          {...{
                            className: clsx(classes.taxTitle, classes.taxText),
                          }}>
                          Honoraires :
                        </Typography>

                        <Typography {...{ className: clsx(classes.taxText) }}>
                          {data.floatingRates.honoraires}€
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Box {...{ display: 'flex', key: index }}>
                      <Typography
                        {...{
                          className: clsx(classes.taxTitle, classes.taxText),
                        }}>
                        {rateInfo.label} :
                      </Typography>

                      <Typography {...{ className: clsx(classes.taxText) }}>
                        {fixedDecimalPercentage(
                          (data.floatingRates as any)[rateInfo.key],
                        )}
                      </Typography>
                    </Box>
                  );
                },
              )}
            </>
          );
        })}

        <Box display="flex" flexDirection="column">
          <Button
            disabled={!canEditRates}
            {...{
              variant: 'contained',
              onClick: () => setOpenEditOtherRatesDialog(true),
              className: clsx(classes.taxTitle, classes.editButton),
            }}>
            <EditIcon />

            <Typography style={{ marginLeft: '5px' }}>Editer</Typography>
          </Button>

          <Button
            variant="contained"
            className={clsx(classes.taxTitle, classes.editButton)}
            onClick={handleGeneratePdf}>
            <PictureAsPdfIcon />
            <Typography style={{ marginLeft: '5px' }}>generer pdf</Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Parameters;
